// 最近事件（給平面圖閃爍 / 總覽用）：跟事件頁同一個來源（/v1/vms/search/events），只留時間與歸屬設備。
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { eventTsMs, type KinioEvent } from '@kinio/web-sdk';
import { CHILD_SEP } from './devices';
import { useLiveEvents } from './liveEvents';
import { useScope } from './scope';
import { searchApi } from './planeApi';

/** 子設備的事件 device_ref 已是 <parent>:chN；記在 parent 的另帶 payload.ch。 */
function keysOf(homeId: string, ev: Pick<KinioEvent, 'device_ref' | 'payload'>): { deviceKey: string; parentKey: string } {
  const ch = ev.payload?.ch;
  const isChild = ev.device_ref.includes(CHILD_SEP);
  const ref = !isChild && typeof ch === 'number' && ch >= 0 ? `${ev.device_ref}${CHILD_SEP}${ch}` : ev.device_ref;
  const parent = isChild ? ev.device_ref.slice(0, ev.device_ref.lastIndexOf(CHILD_SEP)) : ev.device_ref;
  return { deviceKey: `${homeId}/${ref}`, parentKey: `${homeId}/${parent}` };
}

export interface RecentEvent {
  ev: KinioEvent;
  deviceKey: string; // <home_id>/<device_ref>（通道 = <parent>:chN）
  parentKey: string;
  ts: number;
}

/** 輪詢（補齊、剛打開頁面時的歷史）+ 即時串流（/v1/vms/stream，1 秒內到）合併、依 id 去重。 */
export function useRecentEvents(limit = 50, refetchMs = 30_000) {
  const polled = usePolledEvents(limit, refetchMs);
  const { recent } = useLiveEvents();
  const data = useMemo(() => {
    const live: RecentEvent[] = recent.map((l) => ({
      ev: l.event,
      deviceKey: `${l.homeId}/${l.deviceRef}`,
      parentKey: keysOf(l.homeId, l.event).parentKey,
      ts: eventTsMs(l.event),
    }));
    const seen = new Set<string>();
    return [...live, ...(polled.data ?? [])]
      .filter((r) => (seen.has(r.ev.id) ? false : (seen.add(r.ev.id), true)))
      .sort((a, b) => b.ts - a.ts);
  }, [recent, polled.data]);
  return { ...polled, data };
}

function usePolledEvents(limit: number, refetchMs: number) {
  const { visible } = useScope();
  const byApi = new Map<string, string[]>();
  for (const h of visible) byApi.set(h.endpoints.api, [...(byApi.get(h.endpoints.api) ?? []), h.home_id]);
  return useQuery({
    queryKey: ['recent-events', limit, [...byApi.entries()].map(([a, h]) => a + ':' + h.join('+')).join('|')],
    enabled: byApi.size > 0,
    refetchInterval: refetchMs,
    queryFn: async (): Promise<RecentEvent[]> => {
      const lists = await Promise.all(
        [...byApi.entries()].map(async ([api, homes]) => {
          const r = await searchApi.events(api, { q: '', homes, limit });
          return (r?.data ?? []).filter((d) => d.class !== 'bookmark').map((d) => {
            // level：伺服器投影時算好的分級（布防中的偵測也是警報；總覽與地圖「只看警報」用）
            const ev: KinioEvent = { id: d.event_id, device_ref: d.device_ref, type: d.key, event_class: d.class, payload: d.payload, ts: d.occurred_at, ...(d.level ? { level: d.level } : {}) } as KinioEvent;
            return { ev, ...keysOf(d.home_id, ev), ts: eventTsMs(ev) };
          });
        }),
      );
      return lists.flat().sort((a, b) => b.ts - a.ts);
    },
  });
}
